import React from 'react';
import { shape, bool } from 'prop-types';
import { gql, graphql } from 'react-apollo';
import IconButton from 'components/IconButton';
import LoadingIndicator from 'components/LoadingIndicator';
import SpeedDisplay from 'components/SpeedDisplay';
import TimeDisplay from 'components/TimeDisplay';
import formatNextUpdate from 'helpers/formatNextUpdate';
import formatLastUpdate from 'helpers/formatLastUpdate';
import formatInteger from 'helpers/formatInteger';
import formatFloat from 'helpers/formatFloat';
import formatMinutesSeconds from 'helpers/formatMinutesSeconds';
import ChevronIcon from 'icons/chevron.inline.svg';
import cx from 'classnames';

import Layout from '../Layout';
import SegmentHeader from '../SegmentHeader';
import TripletRow from '../../TripletRow';
import TripletItem from '../../TripletItem';
import HorizontalSection from '../../HorizontalSection';
import Chart from '../Chart';
import GridRow from '../../GridRow';
import GridItem from '../../GridItem';
import SpeedTrendIcon from '../../../SpeedTrendIcon';

import styles from './segment.scss';

const SegmentQuery = gql`
  query Segment($id: ID!) {
    segment(id: $id) {
      id
      status
      name
      normalSpeed
      liveSpeed
      speedLimit
      expectedTravelTime
      travelTimeIndex
      liveTravelTime
      numberOfTripsToday
      numberOfTripsSinceLastUpdate
      oppositeSegmentId
      liveTravelTimeToday {
        timestamp
        value
      }
      expectedTravelTimeToday {
        timestamp
        value
      }
      to {
        id
        name
        trackingDevice {
          id
          status
          lastUpdate
          reportInterval
        }
      }
      from {
        id
        name
        trackingDevice {
          id
          status
          lastUpdate
          reportInterval
        }
      }
    }
  }
`;

export function Segment({ data }) {
  if (data.loading) {
    return (
      <Layout
        backTo="/map/live/segments"
        title="All segments"
        header={<SegmentHeader status="ok" fromName="..." toName="..." isLoading />}
      >
        <LoadingIndicator />
      </Layout>
    );
  }

  if (!data.segment) {
    return (
      <div>
        Failed to find segment for id: {data.variables.id}
      </div>
    );
  }

  const {
    id,
    status,
    normalSpeed,
    liveSpeed,
    speedLimit,
    expectedTravelTime,
    liveTravelTime,
    travelTimeIndex,
    numberOfTripsToday,
    numberOfTripsSinceLastUpdate,
    oppositeSegmentId,
    liveTravelTimeToday,
    expectedTravelTimeToday,
    from: { trackingDevice: fromDevice, name: fromLocationName },
    to: { trackingDevice: toDevice, name: toLocationName },
  } = data.segment;

  const isReporting = status !== 'disconnected';

  /* @TODO sort this out. Data based on the two devices. We need to move this to
  GQL probably
  */
  const reportInterval = Math.max(fromDevice.reportInterval, toDevice.reportInterval);
  const lastUpdate = fromDevice.lastUpdate;

  const speedTrend = 'up';

  return (
    <Layout
      backTo="/map/live/segments"
      title="All segments"
      header={
        <SegmentHeader
          status={status}
          fromName={fromLocationName}
          toName={toLocationName}
          oppositeSegmentId={oppositeSegmentId}
        />
      }
    >
      {isReporting
        ? <TripletRow title="Travel time">
            <TripletItem title="live" unitName="min" status={status}>
              <TimeDisplay value={liveTravelTime} status={status} />
              <SpeedTrendIcon
                trend={speedTrend}
                className={cx(styles.trendIcon, {
                  [styles[`status-${status}`]]: status,
                })}
              />
            </TripletItem>
            <TripletItem title="expected" unitName="min">
              <TimeDisplay value={expectedTravelTime} />
            </TripletItem>
            <TripletItem title="index">
              {formatFloat(travelTimeIndex)}
            </TripletItem>
          </TripletRow>
        : <TripletRow title="Travel time">
            <TripletItem title="live" unitName="min" status={status}>
              <span>-</span>
            </TripletItem>
            <TripletItem title="expected" unitName="min" status={status}>
              <span>-</span>
            </TripletItem>
            <TripletItem title="index" status={status}>
              <span>-</span>
            </TripletItem>
          </TripletRow>}

      <Chart
        reportedValues={liveTravelTimeToday}
        expectedValues={expectedTravelTimeToday}
        title={'Number of hits today'}
        calculateCrosshairA={({ reported }) => ({
          value: formatMinutesSeconds(reported, {
            useClockFormat: true,
          }),
          title: 'TT',
        })}
        calculateCrosshairB={({ reported, expected }) => ({
          value: formatFloat(reported / expected),
          title: 'TTI',
        })}
      />

      <GridRow title="Speed">
        <GridItem label="live speed" status={status}>
          {isReporting
            ? <SpeedDisplay status={status} value={liveSpeed} unit="MPH" trend={speedTrend} />
            : '-'}
        </GridItem>
        <GridItem label="speed limit">
          <SpeedDisplay value={speedLimit} unit="MPH" />
        </GridItem>
        <GridItem label="expected speed">
          <SpeedDisplay value={normalSpeed} unit="MPH" />
        </GridItem>
      </GridRow>
      <GridRow title="Updates">
        <GridItem label="trips today">
          {formatInteger(numberOfTripsToday)}
        </GridItem>
        <GridItem label="trips last update">
          {formatInteger(numberOfTripsSinceLastUpdate)}
        </GridItem>
        {isReporting
          ? <GridItem label="next update">
              {formatNextUpdate(lastUpdate, reportInterval)}
            </GridItem>
          : <GridItem label="last update">
              {formatLastUpdate(lastUpdate)}
            </GridItem>}
      </GridRow>
      <HorizontalSection center>
        <IconButton label="details page" linkTo={`/segments/${id}`} disabled asLink iconRight>
          <ChevronIcon />
        </IconButton>
      </HorizontalSection>
    </Layout>
  );
}

Segment.propTypes = {
  data: shape({
    loading: bool.isRequired,
    location: shape({}),
  }).isRequired,
};

const SegmentWithData = graphql(SegmentQuery, {
  options: ({ params }) => ({
    variables: {
      id: params.id,
    },
  }),
})(Segment);

export default SegmentWithData;



// WEBPACK FOOTER //
// ./src/components/DataPanel/Details/Segment/index.js